import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  FormErrors,
  TextInputModes,
  TextInput,
  Checkbox
} from '@lightbeamhealth/blueprint-form';
import Button from '../../molecules/button';
import Card from '../../organisms/card';
import Callout from '../../organisms/callout';
import ErrorCallout from '../../organisms/callout/error-callout';
import PasswordInput from '../../organisms/password';
import Icon from '../../atoms/icon';
import Meta from '../../atoms/meta';
import { faClipboard } from '@fortawesome/pro-solid-svg-icons';
import { useCreateUser } from '../../../libs/user';

interface RegisterData {
  email: string;
  phone: string;
  password: string;
  confirm: string;
  eula: string;
}

const RegisterPage: React.FC = () => {
  const { createUser, data, isLoading, error } = useCreateUser();
  const initialData: RegisterData = {
    email: '',
    phone: '',
    password: '',
    confirm: '',
    eula: ''
  };

  /**
   * The line break in the error string isn't respected so the
   * error message gets all mashed together. The below code
   * splits the error and wraps in a block element so that it's
   * more readable.
   */
  let errorElement = null;
  if (error) {
    const errors = error.split(',');
    errorElement = (
      <React.Fragment>
        {errors.map((err: string) => (
          <span className="block" key={err}>
            {err}
          </span>
        ))}
      </React.Fragment>
    );
  }

  const form = new FormInstance<RegisterData>(initialData);

  function submit(data: RegisterData) {
    createUser({
      email: data.email,
      phone: data.phone,
      password: data.password
    });
  }

  const validate = (values: RegisterData): FormErrors => {
    const errors: FormErrors = {};
    if (values.password !== values.confirm) {
      errors.confirm = 'The two passwords must match!';
    }
    return errors;
  };

  /**
   * The /user/create/ endpoint also logs in
   * the user, so this reloads the page to show
   * the dashboard
   */
  const reload = () => {
    window.location.assign('/');
  };

  /**
   * Reset the form once the user is
   * successfully created
   */
  useEffect(() => {
    form.reset();
  }, [data && data.status === 'OK']);

  return (
    <Card
      style={{
        marginBottom: '100px'
      }}
    >
      <Meta title="Get Started" />
      <h1 className="text-sans-lg mb-7">
        <Icon icon={faClipboard} /> Register an account
      </h1>
      <Form form={form} onSubmit={submit} validate={validate}>
        {error && <ErrorCallout className="mb-8">{errorElement}</ErrorCallout>}
        <TextInput
          autoComplete="email"
          label="Email"
          name="email"
          type="email"
          required
        />
        <TextInput
          label="Contact Phone"
          name="phone"
          type="tel"
          mode={TextInputModes.Phone}
          required
        />
        <PasswordInput
          label="Password"
          name="password"
          showStrength
          enforceStrength
          required
        />
        <TextInput
          label="Confirm Password"
          name="confirm"
          type="password"
          required
        />
        <Checkbox
          labelElement={
            <React.Fragment>
              I agree with the{' '}
              <a
                href="https://www.caresignal.health/eula"
                target="_blank"
                rel="noreferrer"
              >
                End User Agreement
              </a>
            </React.Fragment>
          }
          name="eula"
          requiredErrorMessage="You must agree to the End User Agreement!"
          required
        />
        {data && data.status === 'OK' && (
          <Callout intent="success" className="mb-8">
            User created and logged in successfully!
            <Button
              onClick={reload}
              bare
              variant="minimal"
              className="underline"
            >
              Click here to go to the dashboard.
            </Button>
          </Callout>
        )}
        <Button
          type="submit"
          intent="success"
          disabled={isLoading}
          className="mt-8"
          fill
        >
          Register
        </Button>
      </Form>
    </Card>
  );
};

export default RegisterPage;
